import { Facet, FacetCategory } from 'services/api/entities/catalog/facets'
import { getId, isDefined } from 'services/utils'
import { FacetFilterGroup } from 'types/form'

export type FacetSelectComponentProps<T extends string, K extends string> = {
  source: string
  facetCategory: T
  filterGroup?: K
}

//Functions to parse/compute the facets number[] to send to the BE, based on the new per facet component logic

export type SingleSelectableFacetCategory = Extract<
  FacetCategory,
  'group' | 'pricing' | 'productFilter'
>
export type MultiSelectableFacetsCategory = Extract<
  FacetCategory,
  'tag' | 'productFilter'
>

/**
 * Finds a single facet from specified `filterGroup` and `category`.
 * In case of `filterGroup` being present, the `category` is ignored.
 * @param facets parsed facets
 * @param category category to search for
 * @param filterGroup filterGroup to search for
 *
 * @returns the facet id or null if not found
 */
export const findSingleFacet = (
  facets: Facet[],
  category: SingleSelectableFacetCategory,
  filterGroup?: FacetFilterGroup
) =>
  facets.find((f) =>
    filterGroup ? f.filterGroup === filterGroup : f.category === category
  )?.id ?? null

/**
 * Finds multiple facets from specified `filterGroup` and `category`.
 * In case of `filterGroup` being present, the `category` is ignored.
 * @param facets parsed facets
 * @param category category to search for
 * @param filterGroup filterGroup to search for
 *
 * @returns the facet ids or [] if not found
 */
export const findMultipleFacets = (
  facets: Facet[],
  category: MultiSelectableFacetsCategory,
  filterGroup?: FacetFilterGroup
) =>
  facets
    .filter((f) =>
      filterGroup ? f.filterGroup === filterGroup : f.category === category
    )
    .map(getId)

/**
 * Bundle facets ids into an array filtering out null values.
 * @param facetIds facet ids to bundle
 */
export const bundleFacetIds = (...facetIds: (number | null)[]) => {
  return facetIds.filter(isDefined) as number[]
}
